import React from "react";
import tailwindClassnames from "./TailwindClassnames";
import { t } from "i18next";
import SingleDatePicker from "./SingleDatePicker";
import { useProfile } from "./ProfileProvider";

type DateRangeSelectorProps = {
  label: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  onDateChange: (dates: [Date | null, Date | null]) => void;
  /** Chained after each field's own keydown handling — e.g. a caller
   * submitting a parent search on Enter. Applied to both fields. */
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
};

// Same look as the shared tableFilterDateInput token, but narrower than its
// default w-60 (sized for one combined "start - end" display) since this is
// now two independent fields sitting side by side.
const dateInputClassName = tailwindClassnames.tableFilterDateInput.replace(
  "w-60",
  "w-32",
);

/**
 * Independent start/end date-range fields for table toolbars (e.g.
 * SearchBarSensors, SearchBarDeployments) — one combined range control used
 * to live here (a single `selectsRange` DatePicker), but that meant the
 * live-typing year/month snap and per-keystroke safety (`strictParsing`,
 * see SingleDatePicker's docstring) only really worked cleanly for the
 * start side; the end side never got equivalent typing support. Both sides
 * now render through the same `SingleDatePicker` the review filter modal
 * uses, so they can't drift in behavior — this component just wires their
 * two independent onChange callbacks back into one [start, end] tuple to
 * keep its own external API (`onDateChange`) unchanged for existing callers.
 */
const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  label,
  startDate,
  endDate,
  onDateChange,
  onKeyDown,
}) => {
  const { theme } = useProfile();
  const isDarkMode = theme === "dark";

  const calendarClassName = `custom-calendar ${
    isDarkMode ? "dark-mode-calendar" : ""
  }`;

  return (
    // items-start keeps the label the width of its text so it reads left-aligned
    // like the neighbouring filter labels, whatever the toolbar inherits.
    <div className="flex flex-col items-start">
      <label className={tailwindClassnames.textInputLabel}>{label}</label>
      <div className="flex flex-row items-center gap-2">
        {/* SingleDatePicker's root wrapper is flex-1 (needed by callers like
            ReviewDateRangeInput that want it to fill a full-width modal
            field) — flex-none here keeps it at its compact w-32 instead of
            stretching to fill this toolbar's flex row. */}
        <div className="flex-none">
          <SingleDatePicker
            selected={startDate ?? null}
            onChange={(date) => onDateChange([date, endDate ?? null])}
            placeholder={t("Start Date")}
            ariaLabel={t("Start Date")}
            maxDate={endDate}
            calendarClassName={calendarClassName}
            inputClassName={dateInputClassName}
            todayButtonLabel={t("Today")}
            onKeyDown={onKeyDown}
          />
        </div>
        <span className="text-off-black-light dark:text-greyed-text">—</span>
        <div className="flex-none">
          <SingleDatePicker
            selected={endDate ?? null}
            onChange={(date) => onDateChange([startDate ?? null, date])}
            placeholder={t("End Date")}
            ariaLabel={t("End Date")}
            minDate={startDate}
            calendarClassName={calendarClassName}
            inputClassName={dateInputClassName}
            todayButtonLabel={t("Today")}
            onKeyDown={onKeyDown}
          />
        </div>
      </div>
    </div>
  );
};

export default DateRangeSelector;
